import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ReCAPTCHA from "react-google-recaptcha";
import { environment } from "../../../../utils/environment";
import {
  IAssignment,
  InviteError,
  InviteField,
  InviteUserModalController,
  InviteUserModalControllerDelegate,
} from "./controller";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUCCESS_CLOSE_DELAY_MS = 2500;

interface State {
  isLoading: boolean;
  phase: "form" | "submitting" | "success";
  errorMessage: string;
  errorField?: InviteField;
  assignmentsError: string;
  assignmentsLoading: boolean;
  assignmentsInput: string;
  assignments: IAssignment[];
  selectedAssignments: IAssignment[];
  firstName: string;
  lastName: string;
  email: string;
  recaptchaToken: string;
  /** Delivery outcome of the invite e-mail; `null` = none was owed. */
  emailDelivered: boolean | null;
}

interface Props {
  delegate: InviteUserModalDelegate;
}

export interface InviteUserModalDelegate {
  shouldCloseModal: () => void;
  applicationShouldLogout: () => void;
  didInviteUser: () => void;
}

class InviteUserModal
  extends React.Component<Props, State>
  implements InviteUserModalControllerDelegate {
  controller: InviteUserModalController;
  recaptchaRef = React.createRef<ReCAPTCHA>();
  private assignmentsFetchDebounce?: NodeJS.Timeout;
  private closeTimer?: NodeJS.Timeout;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      phase: "form",
      errorMessage: "",
      errorField: undefined,
      assignmentsError: "",
      assignmentsLoading: false,
      assignmentsInput: "",
      assignments: [],
      selectedAssignments: [],
      firstName: "",
      lastName: "",
      email: "",
      recaptchaToken: "",
      emailDelivered: null,
    };
    this.controller = new InviteUserModalController(this);
  }

  // delegate
  didGetAssignments(assignments: IAssignment[]) {
    this.setState({
      assignments,
      assignmentsError: "",
    });
  }

  didFailGetAssignments(message: string) {
    this.setState({ assignmentsError: message });
  }

  /**
   * The invitation itself succeeded in every branch here; `emailDelivered:
   * false` only means the one e-mail it owed did not reach the person.
   *
   * That case skips the auto-close: the point is that the psychologist reads it
   * and re-sends, and a modal that closes itself after 2.5s would take the
   * warning with it. `null` (none owed) reads as ordinary success.
   */
  didInviteUser(emailDelivered: boolean | null) {
    this.setState({
      phase: "success",
      errorMessage: "",
      errorField: undefined,
      emailDelivered,
    });
    if (emailDelivered === false) {
      return;
    }
    this.closeTimer = setTimeout(
      () => this.props.delegate.didInviteUser(),
      SUCCESS_CLOSE_DELAY_MS
    );
  }

  /**
   * O reCAPTCHA é de uso único: o token já enviado não serve para a próxima
   * tentativa, então o campo volta desmarcado junto com o erro.
   */
  didFailInviteUser(error: InviteError) {
    this.setState({
      phase: "form",
      errorMessage: error.message,
      errorField: error.field,
      recaptchaToken: "",
    });
  }

  applicationShouldLogout() {
    this.setState({ phase: "form" });
    this.props.delegate.applicationShouldLogout();
  }

  handleChangeFirstName(event: any) {
    this.setState({
      firstName: event.target.value,
      ...this.clearFieldError("firstName"),
    });
  }

  handleChangeLastName(event: any) {
    this.setState({
      lastName: event.target.value,
      ...this.clearFieldError("lastName"),
    });
  }

  handleChangeEmail(value: string) {
    this.setState({
      email: value.toLowerCase().trim(),
      ...this.clearFieldError("email"),
    });
  }

  handleChangeSelected(selectedAssignments: IAssignment[]) {
    this.setState({
      selectedAssignments,
      ...this.clearFieldError("assignedApplications"),
    });
  }

  /** Editar o campo recusado apaga o destaque, mas mantém o alerta visível. */
  private clearFieldError(field: InviteField) {
    return this.state.errorField === field
      ? { errorField: undefined }
      : {};
  }

  componentDidMount() {
    this.fetchAssignments("");
  }

  componentWillUnmount() {
    clearTimeout(this.assignmentsFetchDebounce!);
    clearTimeout(this.closeTimer!);
  }

  didClickClose() {
    if (this.state.phase === "submitting") {
      return;
    }
    if (this.state.phase === "success") {
      clearTimeout(this.closeTimer!);
      this.props.delegate.didInviteUser();
      return;
    }
    this.props.delegate.shouldCloseModal();
  }

  async onRecaptchaChange() {
    const recaptchaToken = this.recaptchaRef.current?.getValue() || "";
    this.setState({
      recaptchaToken,
    });
  }

  async didClickInviteUsers() {
    this.setState({
      phase: "submitting",
      errorMessage: "",
      errorField: undefined,
    });
    const token = localStorage.getItem("AccessToken")!;
    const idsToAdd = this.state.selectedAssignments.map(
      (assignment) => assignment.applicationId
    );
    const { firstName, lastName, email, recaptchaToken } = this.state;
    this.controller.inviteUser(
      token,
      firstName,
      lastName,
      email,
      idsToAdd,
      recaptchaToken || ""
    );
  }

  private fetchAssignments(filter: string) {
    this.setState({ assignmentsLoading: true });
    clearTimeout(this.assignmentsFetchDebounce!);
    this.assignmentsFetchDebounce = setTimeout(async () => {
      try {
        const token = localStorage.getItem("AccessToken")!;
        const normalizedFilter = filter.trim();
        const params = {
          page: 1,
          limit: 15,
          ...(normalizedFilter ? { filter: normalizedFilter } : {}),
        };
        await this.controller.getManagedAssignments(token, params);
      } finally {
        this.setState({ assignmentsLoading: false, isLoading: false });
      }
    }, 300);
  }

  private isFormValid() {
    const { firstName, lastName, email, recaptchaToken } = this.state;
    return (
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      EMAIL_REGEX.test(email) &&
      recaptchaToken.length > 0
    );
  }

  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  renderSubmitting() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap={2}
        paddingY={5}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Enviando convite...
        </Typography>
      </Box>
    );
  }

  renderSuccess() {
    // The old copy claimed delivery unconditionally; the route can now say the
    // e-mail did not go out, and the wording has to follow it.
    if (this.state.emailDelivered === false) {
      return (
        <Box
          alignItems="center"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          gap={1.5}
          paddingY={4}
        >
          <MarkEmailUnreadIcon sx={{ fontSize: 56, color: "warning.main" }} />
          <Typography
            variant="subtitle1"
            color="warning.main"
            fontWeight="bold"
          >
            Convite criado, e-mail não enviado
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            O convite, as aplicações e as licenças foram registrados — apenas o
            e-mail para {this.state.email} não saiu. Use "Enviar acesso por
            e-mail" no menu da aplicação para reenviar.
          </Typography>
          <Button
            variant="contained"
            onClick={() => this.didClickClose()}
            sx={{ mt: 1 }}
          >
            Entendi
          </Button>
        </Box>
      );
    }

    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap={1}
        paddingY={5}
      >
        <CheckCircleIcon sx={{ fontSize: 56, color: "success.main" }} />
        <Typography variant="subtitle1" color="success.main" fontWeight="bold">
          Convite enviado!
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Enviamos um e-mail de convite para {this.state.email}.
        </Typography>
      </Box>
    );
  }

  renderForm() {
    const assignmentsById = new Map<number, IAssignment>();
    this.state.selectedAssignments.forEach((assignment) => {
      if (assignment?.applicationId !== undefined) {
        assignmentsById.set(assignment.applicationId, assignment);
      }
    });
    this.state.assignments.forEach((assignment) => {
      if (assignment?.applicationId !== undefined) {
        assignmentsById.set(assignment.applicationId, assignment);
      }
    });
    const assignmentOptions = Array.from(assignmentsById.values());
    const { errorField } = this.state;
    const hasInvalidEmail =
      this.state.email.length > 0 && !EMAIL_REGEX.test(this.state.email);

    return (
      <Box display="flex" flexDirection="column" sx={{ gap: 2 }}>
        {/* Vinculating applications swaps WHICH single e-mail goes out, so the description and the notice
            must never both be on screen claiming different things: this line
            speaks only for the no-applications case. */}
        <Typography color="text.secondary">
          {this.state.selectedAssignments.length === 0
            ? "Digite os dados do usuário. Se ainda não tiver conta, receberá um e-mail para finalizar o cadastro; se já tiver, receberá um aviso do convite."
            : "Digite os dados do usuário e confira as aplicações que serão vinculadas."}
        </Typography>

        {/* One e-mail per invite, and WHICH one turns on two things the modal
            cannot control: whether applications are attached, and whether the
            typed e-mail already belongs to an account. With applications, an
            account-less invitee gets the access codes while an existing account
            gets the plain notice and no code at all — so this states both
            branches rather than promising a code to everyone. */}
        {this.state.selectedAssignments.length > 0 && (
          <Alert severity="info">
            <strong>Se o usuário ainda não tiver conta</strong>, receberá um
            e-mail com{" "}
            {this.state.selectedAssignments.length === 1
              ? "o código de acesso da aplicação vinculada"
              : `os códigos de acesso das ${this.state.selectedAssignments.length} aplicações vinculadas`}
            , e poderá responder sem finalizar o cadastro.{" "}
            <strong>Se já tiver conta</strong>, receberá apenas o aviso do
            convite e entra com e-mail e senha — as aplicações ficam disponíveis
            ao acessar a plataforma.
          </Alert>
        )}

        {this.state.errorMessage && (
          <Alert
            severity="error"
            onClose={() =>
              this.setState({ errorMessage: "", errorField: undefined })
            }
          >
            {this.state.errorMessage}
          </Alert>
        )}

        <TextField
          variant="outlined"
          required
          label="Nome"
          fullWidth
          value={this.state.firstName}
          error={errorField === "firstName"}
          onChange={(event) => this.handleChangeFirstName(event)}
        />
        <TextField
          variant="outlined"
          required
          label="Sobrenome"
          fullWidth
          value={this.state.lastName}
          error={errorField === "lastName"}
          onChange={(event) => this.handleChangeLastName(event)}
        />
        <TextField
          variant="outlined"
          required
          label="E-mail"
          fullWidth
          value={this.state.email}
          error={hasInvalidEmail || errorField === "email"}
          helperText={hasInvalidEmail ? "Informe um e-mail válido." : undefined}
          onChange={(event) => this.handleChangeEmail(event.target.value)}
        />

        {this.state.assignmentsError && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => this.fetchAssignments(this.state.assignmentsInput)}
              >
                Tentar novamente
              </Button>
            }
          >
            {this.state.assignmentsError} Você ainda pode enviar o convite sem
            associar aplicações.
          </Alert>
        )}

        <Autocomplete
          multiple
          options={assignmentOptions}
          getOptionLabel={(assignment: IAssignment) =>
            `${assignment.title} (${assignment.instrumentTitle})`
          }
          value={this.state.selectedAssignments}
          onChange={(_, newValue) => this.handleChangeSelected(newValue)}
          inputValue={this.state.assignmentsInput}
          onInputChange={(_, value) => {
            this.setState({ assignmentsInput: value }, () => {
              this.fetchAssignments(value);
            });
          }}
          loading={this.state.assignmentsLoading}
          disableCloseOnSelect
          isOptionEqualToValue={(option, value) =>
            option.applicationId === value.applicationId
          }
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            const optionKey = option.applicationId ?? key;
            return (
              <li key={optionKey} {...optionProps}>
                <Checkbox checked={selected} />
                {`${option.title} (${option.instrumentTitle})`}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Aplicações"
              error={errorField === "assignedApplications"}
              helperText="Comece a digitar para buscar as aplicações desejadas."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {this.state.assignmentsLoading && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          ListboxProps={{
            style: {
              maxHeight: 200,
            },
          }}
          noOptionsText="Nenhuma aplicação encontrada"
          sx={{
            width: "100%",
          }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            pt: 1,
          }}
        >
          <ReCAPTCHA
            ref={this.recaptchaRef}
            sitekey={environment.google.recaptchaToken}
            onChange={() => this.onRecaptchaChange()}
            onExpired={() => this.setState({ recaptchaToken: "" })}
          />
          {errorField === "recaptchaToken" && (
            <Typography variant="caption" color="error.main">
              Refaça a verificação e envie o convite novamente.
            </Typography>
          )}
        </Box>

      </Box>
    );
  }

  renderContent() {
    if (this.state.isLoading) {
      return this.renderLoading();
    }
    if (this.state.phase === "submitting") {
      return this.renderSubmitting();
    }
    if (this.state.phase === "success") {
      return this.renderSuccess();
    }
    return this.renderForm();
  }

  render() {
    const showActions = !this.state.isLoading && this.state.phase === "form";

    return (
      <Dialog
        open
        onClose={() => this.didClickClose()}
        variant="modal"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle variant="modal">
          <PersonAddIcon />
          Convidar usuário
        </DialogTitle>
        <DialogContent variant="modal">{this.renderContent()}</DialogContent>
        {showActions && (
          <DialogActions
            variant="modal"
            sx={{ flexWrap: "wrap", gap: 1, justifyContent: "end" }}
          >
            <Button
              variant="outlined"
              color="error"
              onClick={() => this.didClickClose()}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={() => this.didClickInviteUsers()}
              disabled={!this.isFormValid()}
            >
              Convidar Usuário
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }
}

export default InviteUserModal;
